import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Role } from '@ads-manager/shared';
import { authApi, AuthResponse } from '../api';
import { queryClient } from '../query-client';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  clearError: () => void;
  checkAuth: () => Promise<void>;
  hasRole: (roles: Role[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response: AuthResponse = await authApi.login({ email, password });

          const { user, tokens } = response;

          // Пользовательский server-state не должен переходить между аккаунтами.
          queryClient.clear();

          // Store tokens in localStorage for API client
          localStorage.setItem('accessToken', tokens.accessToken);
          localStorage.setItem('refreshToken', tokens.refreshToken);

          set({
            user,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (err: unknown) {
          const error = err as { response?: { data?: { message?: string } } };
          set({
            isLoading: false,
            error: error?.response?.data?.message || 'Login failed',
            isAuthenticated: false,
            user: null,
          });
          throw err;
        }
      },

      logout: () => {
        queryClient.clear();
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('auth-storage');

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        });
      },

      setTokens: (accessToken: string, refreshToken: string) => {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        set({ accessToken, refreshToken });
      },

      setUser: (user: User) => {
        if (get().user?.id !== user.id) queryClient.clear();
        set({ user, isAuthenticated: true });
      },

      clearError: () => {
        set({ error: null });
      },

      checkAuth: async () => {
        const accessToken = localStorage.getItem('accessToken');

        if (!accessToken) {
          queryClient.clear();
          set({ isAuthenticated: false, user: null });
          return;
        }

        try {
          const user = await authApi.getProfile();
          if (get().user?.id !== user.id) queryClient.clear();
          set({ user, isAuthenticated: true });
        } catch {
          // Token invalid, clear everything
          queryClient.clear();
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
          });
        }
      },

      hasRole: (roles: Role[]) => {
        const { user } = get();
        if (!user) return false;
        return roles.includes(user.role);
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    }
  )
);

export default useAuthStore;
